import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Paper,
    Typography,
    TextField,
    Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth, useNavigation, useLoading, useSnackbar } from "../../Context/Contexts";
import HistoryIcon from "@mui/icons-material/History";
import BlockIcon from "@mui/icons-material/Block";
import { ListQuery } from "../../Types/ExportTypes";
import CenteredListCard from "../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../components/List/ListCardHeader";
import { useTranslation } from "react-i18next";
import { CustomerAutoComplete } from "../../components/AutoComplete/AutoCompletes";
import { buildQueryFilter } from "../../Utils/QueryFilterUtils";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import LoadingSection from "../../components/LoadingSection";
import ResponsivePagination from "../../components/ResponsivePagination";
interface SalesDocumentListViewModel {
    no: string;
    selltoCustomerNo?: string | null;
    selltoCustomerName?: string | null;
    selltoAddress?: string | null;
    billToCustomerNo?: string | null;
    billToName?: string | null;
    billtoAdress?: string | null;
    xOrderStatus?: number | null;
}


const HistorySalesDocumentList: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const { t } = useTranslation();

    const { documentType = 1 } = params || {};
    const [documents, setDocuments] = useState<SalesDocumentListViewModel[]>([]);
    const [totalDocuments, setTotalDocuments] = useState(0);
    const [query, setQuery] = useCachedListQuery({
        page: 0,
        pageSize: 10,
        filter: "",
        filters: [],
    }, `historySalesDocumentList_${documentType}`);
    const [filter, setFilter] = useState<string>("");
    const [filterProperty, setFilterProperty] = useState<string>("");
    const [loading, setLoading] = useState(false);

    const { authService } = useAuth();
    const { navigate } = useNavigation();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const isBooleanProperty = filterProperty === "ext_Cancelled";
    useEffect(() => {
        if (isBooleanProperty) setFilter("true");
    }, [isBooleanProperty]);

    const fetchDocuments = async () => {
        setLoading(true);
        try {
            const response = await authService.post<{ items: SalesDocumentListViewModel[]; count: number }>(
                `/salesDocumentApi/GetHistoricDocumentList?documentType=${documentType}`,
                query
            );
            setDocuments(response.items);
            setTotalDocuments(response.count);
        } catch (error: any) {
            showSnackbar(`${t("common.failedToFetchOrders")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, [query, documentType]);

    useEffect(() => {
        if (loading) showLoading();
        else hideLoading();
    }, [loading, showLoading, hideLoading]);

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        var localFilterProperty = filterProperty;
        //if (!filter) return;
        if (!localFilterProperty?.trim() && filter?.trim()) {
            localFilterProperty = "Default";
        }

        if (localFilterProperty?.trim() && (filter?.trim() || isBooleanProperty)) {
            const newFilter = buildQueryFilter(localFilterProperty, filter);
            const isDuplicate = query.filters?.some(f =>
                f.property === newFilter.property && f.value === newFilter.value
            );
            if (isDuplicate) return;

            const newFilters = [...(query.filters ?? []), newFilter];
            setQuery({ ...query, filters: newFilters, page: 0, filter: "" });
        } else {
            // No filterProperty: just update the query with the raw filter string
            setQuery({ ...query, page: 0, filter });
        }

        setFilter("");
        setFilterProperty("");
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setQuery({ ...query, pageSize: newPageSize, page: 0 });
    };

    const handleSelectDocument = (documentNo: string) => {
        navigate({ path: "/sales/history/document", params: { documentNo, documentType } });
    };




    return (
        <CenteredListCard
            title={
                documentType == 1
                    ? t("common.orders")
                    : documentType == 2
                        ? t("common.invoices")
                        : t("common.credits")
            }
        >


                {/* Search Field */}
                <Box
                    component="form"
                    onSubmit={handleSearchSubmit}
                    sx={{
                        display: "flex",
                        gap: 2,
                        marginBottom: 2,
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={5} lg={2}>
                            <TextField
                                select
                                label={t("common.field")}
                                value={filterProperty}
                                onChange={(e) => setFilterProperty(e.target.value)}
                                SelectProps={{ native: true }}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            >
                                <option value="">{t("common.default")}</option>
                                <option value="ext_No">{t("common.number")}</option>
                                <option value="ext_SelltoCustomerNo">{t("common.customerNo")}</option>
                                <option value="ext_BilltoCustomerNo">{t("common.billToCustomer")}</option>
                                {documentType == 1 && (
                                    <option value="ext_Cancelled">{t("common.cancelled")}</option>
                                )}
                            </TextField>
                        </Grid>
                        <Grid item xs={7} lg={7}>
                            {(() => {
                                switch (filterProperty) {
                                    case "ext_SelltoCustomerNo":
                                        return (
                                            <CustomerAutoComplete onChange={(o) => setFilter(o?.value || "")} />
                                        );
                                    case "ext_BilltoCustomerNo":
                                        return (
                                            <CustomerAutoComplete onChange={(o) => setFilter(o?.value || "")} />
                                        );

                                    case "ext_Cancelled":
                                        return (
                                            <TextField
                                                select
                                                label={t("common.value")}
                                                variant="outlined"
                                                fullWidth
                                                onChange={(e) => setFilter(e.target.value)}
                                                value={filter}
                                                disabled={loading}
                                                SelectProps={{ native: true }}
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                            >
                                                <option value="true">{t("common.yes")}</option>
                                                <option value="false">{t("common.no")}</option>
                                            </TextField>

                                        )
                                    default:
                                        return (
                                            <TextField
                                                label={t("common.search")}
                                                variant="outlined"
                                                fullWidth
                                                onChange={(event) => setFilter(event.target.value)}
                                                value={filter}
                                                disabled={loading}
                                            />
                                        );
                                }
                            })()}
                         
                        </Grid>
                        <Grid item xs={12} lg={3}>
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={loading}
                            >
                                {t("common.search")}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                    {query.filters?.map((f, index) => (
                        <Chip
                            key={index}
                            label={`${f.property === "Default" ? t("common.default") : f.property}: ${f.value}`}
                            onDelete={() => {
                                const newFilters = query.filters?.filter((_, i) => i !== index);
                                setQuery({ ...query, filters: newFilters, page: 0 });
                            }}
                            deleteIcon={<DeleteIcon />}
                        />
                    ))}
                </Box>

                {/* Card Layout for Orders */}
                <LoadingSection loading={loading}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        {documents.map((document) => (
                            <Grid item
                                xs={12}
                                sm={documents.length === 1 ? 12 : documents.length === 2 ? 6 : 6}
                                md={documents.length === 1 ? 12 : documents.length === 2 ? 6 : 4}
                                key={document.no}>
                                <Card
                                    onClick={() => handleSelectDocument(document.no)}
                                    sx={{
                                        cursor: "pointer",
                                        transition: "box-shadow 0.2s ease-in-out",
                                        "&:hover": {
                                            boxShadow: 4,
                                            backgroundColor: "secondary.main",
                                        },
                                    }}
                                >
                                    <CardContent>
                                        <ListCardHeader
                                            icon={<HistoryIcon fontSize="small" color="action" />}
                                            title={document.no}
                                            status={document.xOrderStatus == 5
                                                ? {
                                                    label: t("common.cancelled"),
                                                    color: "warning",
                                                    icon: <BlockIcon fontSize="small" />,
                                                }
                                                : undefined}
                                        />
                                        <Typography variant="body2" color="textSecondary">
                                            {t("common.customerNo")}: {documentType === 1 ? document.selltoCustomerNo : document.billToCustomerNo}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {t("common.name")}: {documentType === 1 ? document.selltoCustomerName : document.billToName}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {t("common.address")}:{documentType === 1 ? document.selltoAddress : document.billtoAdress}
                                        </Typography>
                                        {document.xOrderStatus == 5 && (
                                            <Typography variant="body2" color="textSecondary">
                                                {t("common.cancelled")}: {t("common.yes")}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                        {documents.length === 0 && (
                            <Grid item xs={12}>
                                <Typography align="center">{t("common.noEntriesFound")}</Typography>
                            </Grid>
                        )}
                    </Grid>
                </LoadingSection>

                {/* Pagination */}
                <ResponsivePagination

                    page={query.page}

                    pageSize={query.pageSize}

                    totalCount={totalDocuments}

                    onPageChange={handlePageChange}

                />
       
        </CenteredListCard>
    );
};

export default HistorySalesDocumentList;